import { useEffect, useState } from 'react'
import { AuthProvider, useAuth } from './lib/auth'
import { SignIn } from './components/SignIn'
import { Chat, type ChatTurn } from './components/Chat'
import { Sidebar, type MainView } from './components/Sidebar'
import { MorningBrief } from './components/MorningBrief'
import { Scorecards } from './components/Scorecards'
import { AdminFeedback } from './components/AdminFeedback'
import { MyContext } from './components/MyContext'
import { fetchHistoryEntry, fetchHistoryThread, fetchMe, primeOpportunity } from './lib/api'
import type { BriefDemo, ChatAttachment, ChatScope, HistoryEntry, HistoryTurn, ScorecardCard } from './lib/types'

// A logged turn, as the Chat view holds it.
function turnFromHistory(full: HistoryTurn): ChatTurn {
  return {
    id: full.audit_id,
    question: full.question,
    response: {
      ok: full.ok,
      audit_id: full.audit_id,
      answer: full.answer || '',
      error: full.error,
      tools_called: full.tools_called || [],
      definitions_applied: full.definitions_applied || [],
      model_used: full.model_used || 'unknown',
      input_tokens: full.input_tokens || 0,
      output_tokens: full.output_tokens || 0,
      total_cost_usd: full.total_cost_usd || 0,
      total_duration_ms: full.total_duration_ms || 0,
    },
    loading: false,
    error: full.error,
  }
}

function AppInner() {
  const { user } = useAuth()
  const [refreshKey, setRefreshKey] = useState(0)
  const [restoredTurns, setRestoredTurns] = useState<ChatTurn[] | null>(null)
  // Set alongside restoredTurns when the restored entry is a whole conversation
  // (2026-09-30). The Chat then CONTINUES that conversation on the next question instead
  // of clearing it. Null for pre-threading rows, which keep the single-turn behaviour.
  const [restoredConversationId, setRestoredConversationId] = useState<string | null>(null)
  const [loadingRestore, setLoadingRestore] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [view, setView] = useState<MainView>('chat')
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  // Sales management (MANAGER_USERS on the server): gates the demo scorecards view.
  const [isManager, setIsManager] = useState<boolean>(false)
  // Account-scoped chat (2026-09-22). Set when a rep opens a chat from a demo card.
  const [scope, setScope] = useState<ChatScope | null>(null)
  // A document attached to the current conversation (2026-09-30). Held here, like scope,
  // because the Chat unmounts when the user visits another view and "Attach to chat" on
  // the My context page has to survive the switch.
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null)
  // Text the My context page wants in the composer ("Run my … workflow for "). Consumed
  // once by the Chat and cleared.
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null)

  // Ask the backend whether the signed-in user is an admin, so we can
  // show/hide the feedback moderation entry point. Runs once per sign-in.
  useEffect(() => {
    if (!user) {
      setIsAdmin(false)
      setIsManager(false)
      return
    }
    let cancelled = false
    fetchMe()
      .then((r) => { if (!cancelled) { setIsAdmin(!!r.is_admin); setIsManager(!!r.is_manager) } })
      .catch(() => { if (!cancelled) { setIsAdmin(false); setIsManager(false) } })
    return () => { cancelled = true }
  }, [user])

  // A manager tapped "Ask Marker about this demo" on a scorecard: same scoped chat as the
  // Morning Brief's "Ask about this demo", primed on the demo's opportunity.
  function handleAskAboutScorecard(card: ScorecardCard) {
    if (!card.opportunity_id) return
    setScope({
      opportunityId: card.opportunity_id,
      account: card.account_name || card.call_title || 'this demo',
      title: card.call_title || undefined,
      kind: 'first',
      time: card.demo_date,
    })
    setRestoredTurns(null)
    setRestoredConversationId(null)
    setAttachment(null)
    setView('chat')
    void primeOpportunity(card.opportunity_id)
  }

  if (!user) {
    return <SignIn />
  }

  // A rep tapped "Ask about this demo". Switch to chat immediately and start warming the
  // opportunity's context in the background — the point of the feature is that the wait
  // happens here, while they are still reading, rather than after their first question.
  // Nothing is awaited: if priming fails or is slow the chat still opens and answers, just
  // at the speed it always did.
  function handleAskAboutDemo(demo: BriefDemo) {
    if (!demo.opportunity_id) return
    setScope({
      opportunityId: demo.opportunity_id,
      account: demo.account,
      title: demo.title,
      kind: demo.kind,
      time: demo.time,
    })
    setRestoredTurns(null)
    setRestoredConversationId(null)
    setAttachment(null)
    setView('chat')
    void primeOpportunity(demo.opportunity_id)
  }

  async function handleSelectHistory(entry: HistoryEntry) {
    // Selecting a history entry always brings us back to chat view. It also drops any
    // account scope: a historical answer is its own context, and silently carrying a
    // different account's primed bundle into it would be worse than having none.
    setView('chat')
    setScope(null)
    setLoadingRestore(true)
    setRestoreError(null)
    try {
      if (entry.conversation_id) {
        // A threaded conversation: restore every turn and let the next question continue
        // it. Whether it had one turn or eight, it is the same conversation.
        const thread = await fetchHistoryThread(entry.conversation_id)
        setRestoredTurns(thread.turns.map(turnFromHistory))
        setRestoredConversationId(thread.conversation_id)
        // Carry the attachment the thread was last using, so continuing it keeps the
        // document in front of the model. The server re-checks ownership and expiry on
        // the next question; a vanished document is reported, never silently dropped.
        const last = [...thread.turns].reverse().find((t) => t.context_applied?.document_id)
        const ctx = last?.context_applied
        setAttachment(ctx?.document_id
          ? { documentId: ctx.document_id, title: ctx.document_title || 'attachment' }
          : null)
      } else {
        // Logged before conversation ids were sent: one turn, shown as reference.
        const full = await fetchHistoryEntry(entry.audit_id)
        setRestoredTurns([turnFromHistory(full)])
        setRestoredConversationId(null)
        setAttachment(null)
      }
    } catch (e) {
      setRestoreError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoadingRestore(false)
    }
  }

  function handleExitRestored() {
    setRestoredTurns(null)
    setRestoredConversationId(null)
    setRestoreError(null)
  }

  // "Run in chat" on the My context page: open the chat with the invocation started so
  // the user only has to name the account or call.
  function handleRunWorkflow(title: string) {
    setPendingPrompt(`Run my "${title}" workflow for `)
    setView('chat')
  }

  function handleAttachFromPanel(att: ChatAttachment) {
    setAttachment(att)
    setView('chat')
  }

  return (
    <div className="flex h-screen bg-marker-bg">
      <Sidebar
        refreshKey={refreshKey}
        onSelectHistory={handleSelectHistory}
        loadingRestore={loadingRestore}
        isAdmin={isAdmin}
        isManager={isManager}
        currentView={view}
        onSwitchView={(v) => setView(v)}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        {view === 'chat' && (
          <Chat
            restoredTurns={restoredTurns || undefined}
            restoredConversationId={restoredConversationId}
            onExitRestored={handleExitRestored}
            onTurnComplete={() => setRefreshKey((k) => k + 1)}
            scope={scope}
            onClearScope={() => setScope(null)}
            attachment={attachment}
            onAttachmentChange={setAttachment}
            pendingPrompt={pendingPrompt}
            onPendingPromptConsumed={() => setPendingPrompt(null)}
          />
        )}
        {view === 'brief' && <MorningBrief onAskAboutDemo={handleAskAboutDemo} />}
        {view === 'scorecards' && isManager && <Scorecards onAskAboutDemo={handleAskAboutScorecard} />}
        {view === 'me' && (
          <MyContext onRunWorkflow={handleRunWorkflow} onAttachDocument={handleAttachFromPanel} />
        )}
        {view === 'admin' && <AdminFeedback />}
        {restoreError && (
          <div className="bg-marker-bad/20 border border-marker-bad text-marker-bad px-4 py-2 text-sm">
            Could not load history entry: {restoreError}
          </div>
        )}
      </div>
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  )
}
